import {apiUrl, isExternalRequestDisabled, disabledIntegrationResponse} from 'utils/deployment';
import 'isomorphic-fetch';
import keys from 'lodash/keys';
import reduce from 'lodash/reduce';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import isNaN from 'lodash/isNaN';
import isUndefined from 'lodash/isUndefined';

import {notify} from "utils/notify";

const Methods = {
    get: 'GET',
    post: 'POST'
};

const contentTypes = {
    json: {
        cb: 'json',
        type: 'application/json'
    },
    blob: {
        cb: 'blob',
        type: 'application/vnd.ms-excel'
    },
    text: {
        cb: 'text',
        type: 'text/html'
    },
    pdf: {
        cb: 'blob',
        type: 'application/pdf'
    },
    formData: {
        type: 'application/x-www-form-urlencoded'
    }
};

const requestTypes = {
    formData: 'formData',
    json: 'json'
};

const omitUndefinedValues = payload => [...keys(payload)].reduce((acc, key) => ({
    ...acc,
    ...(!isUndefined(payload[key]) && {[key]: payload[key]})
}), {});

const requestFormatMethods = {
    mutatePayload: ({payload, isApp}) => {
        return omitUndefinedValues({...payload});
    },
    [requestTypes.formData]: {
        contentType: contentTypes.formData.type,
        cb: ({payload, isApp}) => {
            const data = requestFormatMethods.mutatePayload({payload, isApp});
            return new URLSearchParams(keys(data).reduce((formData, key) => {
                if (isArray(data[key])) {
                    data[key].map(item => formData.append(`${key}[]`, item))
                } else {
                    formData.append(key, data[key]);
                }
                return formData;
            }, new FormData()));
        }
    },
    [requestTypes.json]: {
        contentType: contentTypes.json.type,
        cb: ({payload, isApp}) => JSON.stringify(requestFormatMethods.mutatePayload({payload, isApp}))
    }
};

/**
 * Build url query from "dict" or list of (key, value) pair
 *
 *
 * Example:
 *      build_query({"field1": "val1", "field2": "val2", "field3": "val3"})
 *
 *      or
 *
 *      build_query([["field1", "val1"], ["field2", "val2"], ["field3", "val3"]])
 *
 *      or
 *
 *      build_query({"field1": ["val1", "val2"]}, {"field2": ["val3", "val4"]})
 *
 * returns:
 *
 *      "field1=val1&field2=val2&field3=val3&"
 *
 */

const returnAccEncodedLine = (acc, key, value) => {
    if ((!isEmpty(value) || isNumber(value) || isBoolean(value)) && !isNaN(value)) {
        return acc + encodeURIComponent(key) + "=" + encodeURIComponent(value) + "&";
    }

    return acc;
};

export const buildQueries = (payload, {isPure} = {}) => {
    let queries = '';

    if (payload) {
        if (isArray(payload)) {
            queries = payload.reduce((acc, next) => returnAccEncodedLine(acc, next[0], next[1]), '');
        } else {
            queries = reduce(payload, (acc, value, key) => {
                if (isArray(value)) {
                    return acc + value.reduce((acc, next) => returnAccEncodedLine(acc, `${key}[]`, next), '');
                }

                return returnAccEncodedLine(acc, key, value);
            }, '');
        }
    }

    return queries && isPure ? `?${queries.slice(0, -1)}` : queries;
};

const transformQueryParams = ({payload, isGetMethod, flatQueries, isApp}) => {
    if (!isGetMethod) return '';
    let queries = buildQueries(payload);
    if (flatQueries) queries += `${flatQueries}&`;

    return queries ? queries.slice(0, -1) : '';
};

const getHeaders = ({requestType} = {}) => {
    const headers = {
        "X-Revision": __REVISION__,
        "Content-Type": requestType
            ? requestFormatMethods[requestType].contentType
            : requestFormatMethods.json.contentType
    };

    const jwtToken = localStorage.getItem('jwt');

    if (localStorage.getItem('jwt')) {
        headers["authorization"] = `Token ${jwtToken}`;
    }

    return headers;
};

const getGetParams = () => ({
    method: Methods.get,
    credentials: 'include',
    headers: getHeaders()
});

const getPostParams = ({payload, method, requestType, isApp, isStaff}) => {
    const realRequestType = (isStaff || !isApp) ? requestTypes.json : requestType;
    return ({
        method,
        credentials: 'include',
        body: requestFormatMethods[realRequestType].cb({isApp, payload}),
        headers: getHeaders({requestType: realRequestType})
    })
};

const getUrl = ({url, payload, isGetMethod, flatQueries, isApp, isStaff, isOnboarding, isFull}) => {
    const queries = transformQueryParams({payload, isGetMethod, flatQueries, isApp});
    let realUrl = `${url}${isEmpty(queries) ? '' : '?'}${queries}`;

    if (isFull) {
        return realUrl;
    } else if (isOnboarding) {
        realUrl = `${realUrl}`
    } else if (isStaff) {
        realUrl = `/staff${realUrl}`
    } else if (isApp) {
        realUrl = `/app${realUrl}`;
    }

    return apiUrl(realUrl);
};

const errorStatusChecker = ({code, message, errors} = {}, {status} = {}, {container, containerId}) => {
    const translate = window.__ || ((string) => string);

    if (code === 301 && message) {
        window.location.replace(message + window.location.search);
        return true
    }

    if (code === 409 || code === 401 || (code === 301 && !message)) {
        window.location.reload();
        return true;
    }

    if (code === 402) {
        notify.error(translate("Payment not found. Please contact technical support"));
        return true;
    }

    if (code === 403) {
        notify.error(translate("You have no access to this resource"));
        return true;
    }

    if (code === 415) {
        if (message === 'Image') {
            notify.error(translate("You send unsupported image type. Please upload a jpeg or png"));
        } else {
            notify.error(translate("You send unsupported media type. Please contact technical support"));
        }
        return true;
    }

    if (code === 429) {
        notify.error(translate("You send too many requests"));
        return true;
    }

    if (status !== 404 && status < 500 && message) {
        notify.error(message);
        return true;
    }

    notify.error(translate("Error occurred. Please contact technical support"));
    return true;
};

const getResponseType = (responseType) => {
    if (!responseType.indexOf(contentTypes.json.type)) {
        return contentTypes.json.cb;
    } else if (!responseType.indexOf(contentTypes.text.type)) {
        return contentTypes.text.cb;
    } else if (!responseType.indexOf(contentTypes.blob.type)) {
        return contentTypes.blob.cb;
    } else if (!responseType.indexOf(contentTypes.pdf.type)) {
        return contentTypes.pdf.cb;
    }

    return contentTypes.json.cb;
};

export default ({
    url,
    method = Methods.get,
    payload,
    requestType = requestTypes.json,
    container,
    containerId,
    sccb,
    ercb,
    isApp = true,
    isStaff,
    isOnboarding,
    isFull,
    extraResponseData
}) => {
    if (isExternalRequestDisabled(url, method)) {
        const response = disabledIntegrationResponse();
        if (ercb) ercb(response);
        else notify.error((window.__ || (message => message))(response.message));
        return Promise.resolve(response);
    }
    let extraData = null;
    // check url with queries params
    const splitUrlBySearchParam = url.split('?');
    const realUrl = splitUrlBySearchParam.length > 1
        ? splitUrlBySearchParam[0]
        : url;

    return fetch(
        getUrl({url: realUrl, payload, isApp, isStaff, isOnboarding, isFull, flatQueries: splitUrlBySearchParam[1], isGetMethod: method === Methods.get}),
        method === Methods.get ? getGetParams() : getPostParams({payload, method, requestType, isStaff, isApp})
    ).then(response => {
        if (extraResponseData) extraData = extraResponseData(response);
        if (response.status >= 200 && response.status < 300) {
            return Promise.resolve(response);
        } else {
            return Promise.reject(response);
        }
    }).then(res => {
        return res[getResponseType(res.headers.get("Content-Type"))]().then(response => {
            sccb && sccb(response, extraData);
            return response;
        });
    }).catch((res) => {
        if (res[contentTypes.json.cb]) {
            return res[contentTypes.json.cb]().then(response => {
                ercb && ercb(response);
                !response.errors && errorStatusChecker(response, res, {container, containerId});
                return response;
            });
        } else {
            console.warn(`Has warn: ${res}`);
            return res;
        }
    });
};
